import React from "react";

const getTimelineOwnerText = (item) => {
  const fromName = String(item?.fromAssignedEmployeeName || "").trim();
  const toName = String(item?.toAssignedEmployeeName || "").trim();

  if (fromName && toName && fromName !== toName) {
    return `${fromName} -> ${toName}`;
  }

  if (toName) {
    return toName;
  }

  return "-";
};

const getTimelineNoteText = (item) => {
  const note = String(item?.note || "").trim();
  return note || "No additional note.";
};

export function TaskTimelinePdfDocument({
  components,
  formatTaskDate,
  task,
  timeline,
  forwardCount,
  forwardLocked,
  generatedAt,
}) {
  const { Document, Page, StyleSheet, Text, View } = components;

  const styles = StyleSheet.create({
    page: {
      paddingTop: 26,
      paddingBottom: 28,
      paddingHorizontal: 28,
      backgroundColor: "#f8fafc",
      fontSize: 9.5,
      color: "#0f172a",
      fontFamily: "HindSiliguri",
    },
    headerBand: {
      backgroundColor: "#0f172a",
      borderRadius: 10,
      paddingVertical: 16,
      paddingHorizontal: 18,
      marginBottom: 14,
    },
    headerTopRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 8,
    },
    brand: {
      fontSize: 9,
      color: "#cbd5e1",
      letterSpacing: 1.1,
      textTransform: "uppercase",
    },
    reportType: {
      fontSize: 9,
      color: "#e2e8f0",
    },
    reportTitle: {
      fontSize: 18,
      fontWeight: 700,
      color: "#ffffff",
      marginBottom: 6,
    },
    reportSubtitle: {
      fontSize: 9.5,
      color: "#cbd5e1",
      lineHeight: 1.5,
    },
    metaStrip: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "stretch",
      marginBottom: 14,
      gap: 10,
    },
    metaCard: {
      flexGrow: 1,
      flexBasis: 0,
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 10,
      padding: 10,
    },
    metaLabel: {
      fontSize: 7.5,
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: 0.8,
      marginBottom: 4,
    },
    metaValue: {
      fontSize: 12,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 2,
    },
    metaHelper: {
      fontSize: 8.5,
      color: "#475569",
      lineHeight: 1.45,
    },
    section: {
      marginBottom: 14,
    },
    sectionTitle: {
      fontSize: 11.5,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 8,
    },
    infoTable: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 7,
      overflow: "hidden",
    },
    infoRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#e2e8f0",
    },
    infoRowLast: {
      borderBottomWidth: 0,
    },
    infoLabelCell: {
      width: "22%",
      backgroundColor: "#f8fafc",
      paddingVertical: 6,
      paddingHorizontal: 8,
      borderRightWidth: 1,
      borderRightColor: "#e2e8f0",
    },
    infoValueCell: {
      width: "28%",
      paddingVertical: 6,
      paddingHorizontal: 8,
      borderRightWidth: 1,
      borderRightColor: "#e2e8f0",
    },
    infoLabelCellSecond: {
      width: "22%",
      backgroundColor: "#f8fafc",
      paddingVertical: 6,
      paddingHorizontal: 8,
      borderRightWidth: 1,
      borderRightColor: "#e2e8f0",
    },
    infoValueCellSecond: {
      width: "28%",
      paddingVertical: 6,
      paddingHorizontal: 8,
    },
    infoLabelText: {
      fontSize: 7.1,
      fontWeight: 700,
      color: "#334155",
      textTransform: "uppercase",
      letterSpacing: 0.35,
    },
    infoValueText: {
      fontSize: 8.4,
      color: "#0f172a",
      lineHeight: 1.3,
    },
    infoValueStrong: {
      fontSize: 8.6,
      fontWeight: 700,
      color: "#0f172a",
      lineHeight: 1.3,
    },
    activityTable: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      overflow: "hidden",
    },
    activityHeader: {
      flexDirection: "row",
      backgroundColor: "#e2e8f0",
      borderBottomWidth: 1,
      borderBottomColor: "#cbd5e1",
      paddingVertical: 8,
    },
    activityHeaderText: {
      fontSize: 7.8,
      fontWeight: 700,
      color: "#334155",
      textTransform: "uppercase",
      letterSpacing: 0.45,
      paddingHorizontal: 8,
    },
    columnSerial: {
      width: "7%",
    },
    columnDate: {
      width: "16%",
    },
    columnAction: {
      width: "17%",
    },
    columnActor: {
      width: "14%",
    },
    columnOwner: {
      width: "17%",
    },
    columnStatus: {
      width: "11%",
    },
    columnNote: {
      width: "18%",
    },
    activityRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#e2e8f0",
      paddingVertical: 7,
    },
    activityRowAlt: {
      backgroundColor: "#f8fafc",
    },
    activityRowLast: {
      borderBottomWidth: 0,
    },
    cellText: {
      fontSize: 8.4,
      color: "#0f172a",
      lineHeight: 1.45,
      paddingHorizontal: 8,
    },
    cellTextStrong: {
      fontSize: 8.4,
      fontWeight: 700,
      color: "#0f172a",
      lineHeight: 1.45,
      paddingHorizontal: 8,
    },
    emptyState: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      paddingVertical: 16,
      paddingHorizontal: 14,
    },
    emptyStateText: {
      fontSize: 9,
      color: "#475569",
      lineHeight: 1.5,
      textAlign: "center",
    },
    footer: {
      marginTop: 8,
      paddingTop: 8,
      borderTopWidth: 1,
      borderTopColor: "#cbd5e1",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    footerText: {
      fontSize: 8,
      color: "#64748b",
    },
  });

  const infoRows = [
    [
      { label: "Task Title", value: task?.title || "-" },
      { label: "Current Status", value: task?.status || "-" },
    ],
    [
      { label: "Current Owner", value: task?.assignedEmployeeName || "Unassigned" },
      { label: "Current Progress", value: `${task?.progressPercent || 0}%` },
    ],
    [
      { label: "Priority", value: task?.priority || "-" },
      { label: "Due Date", value: formatTaskDate(task?.dueDate) },
    ],
    [
      { label: "Ticket Number", value: task?.ticketNumber || "Manual task" },
      { label: "Ticket Subject", value: task?.ticketSubject || "No linked ticket subject" },
    ],
    [
      { label: "Created By", value: task?.createdByName || "-" },
      { label: "Created At", value: formatTaskDate(task?.createdAt, true) },
    ],
    [
      { label: "Total Forwards", value: String(forwardCount) },
      { label: "Forward Lock", value: forwardLocked ? "Locked" : "Open" },
    ],
  ];

  const summaryCards = [
    {
      label: "Active Owner",
      value: task?.assignedEmployeeName || "Unassigned",
      helper: task?.assignedEmployeeEmail || task?.assignedUserId || "No contact available",
    },
    {
      label: "Task Status",
      value: task?.status || "Pending",
      helper: `Progress ${task?.progressPercent || 0}% | Due ${formatTaskDate(task?.dueDate)}`,
    },
    {
      label: "Forward Summary",
      value: `${forwardCount} handoff${forwardCount === 1 ? "" : "s"}`,
      helper: forwardLocked ? "Current step is locked for forwarding." : "Current step is open for forwarding.",
    },
  ];

  return (
    <Document
      title={`${task?.title || "Task"} Timeline Report`}
      author="CRM Task Desk"
      subject="Task timeline report"
      creator="CRM Task Desk"
      producer="CRM Task Desk"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.headerBand}>
          <View style={styles.headerTopRow}>
            <Text style={styles.brand}>CRM Task Desk BanglaFire</Text>
            <Text style={styles.reportType}>Task Timeline Report</Text>
          </View>
          <Text style={styles.reportTitle}>{task?.title || "Task Timeline Report"}</Text>
          <Text style={styles.reportSubtitle}>
            Standard report showing task ownership, workflow movement, and complete activity history for review and documentation.
          </Text>
        </View>

        <View style={styles.metaStrip}>
          {summaryCards.map((card) => (
            <View key={card.label} style={styles.metaCard}>
              <Text style={styles.metaLabel}>{card.label}</Text>
              <Text style={styles.metaValue}>{card.value}</Text>
              <Text style={styles.metaHelper}>{card.helper}</Text>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Task Information</Text>
          <View style={styles.infoTable}>
            {infoRows.map((row, index) => (
              <View
                key={`${row[0].label}-${row[1].label}`}
                style={[styles.infoRow, index === infoRows.length - 1 ? styles.infoRowLast : null]}
              >
                <View style={styles.infoLabelCell}>
                  <Text style={styles.infoLabelText}>{row[0].label}</Text>
                </View>
                <View style={styles.infoValueCell}>
                  <Text style={index < 2 ? styles.infoValueStrong : styles.infoValueText}>{row[0].value}</Text>
                </View>
                <View style={styles.infoLabelCellSecond}>
                  <Text style={styles.infoLabelText}>{row[1].label}</Text>
                </View>
                <View style={styles.infoValueCellSecond}>
                  <Text style={index < 2 ? styles.infoValueStrong : styles.infoValueText}>{row[1].value}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Activity Timeline</Text>
          {timeline.length ? (
            <View style={styles.activityTable}>
              <View style={styles.activityHeader}>
                <Text style={[styles.activityHeaderText, styles.columnSerial]}>#</Text>
                <Text style={[styles.activityHeaderText, styles.columnDate]}>Date</Text>
                <Text style={[styles.activityHeaderText, styles.columnAction]}>Action</Text>
                <Text style={[styles.activityHeaderText, styles.columnActor]}>Actor</Text>
                <Text style={[styles.activityHeaderText, styles.columnOwner]}>Movement / Owner</Text>
                <Text style={[styles.activityHeaderText, styles.columnStatus]}>Status</Text>
                <Text style={[styles.activityHeaderText, styles.columnNote]}>Note</Text>
              </View>

              {timeline.map((item, index) => (
                <View
                  key={item?.id || `${item?.createdAt}-${index}`}
                  style={[
                    styles.activityRow,
                    index % 2 === 1 ? styles.activityRowAlt : null,
                    index === timeline.length - 1 ? styles.activityRowLast : null,
                  ]}
                  wrap={false}
                >
                  <Text style={[styles.cellTextStrong, styles.columnSerial]}>{index + 1}</Text>
                  <Text style={[styles.cellText, styles.columnDate]}>{formatTaskDate(item?.createdAt, true)}</Text>
                  <Text style={[styles.cellTextStrong, styles.columnAction]}>{item?.actionLabel || "Task Updated"}</Text>
                  <Text style={[styles.cellText, styles.columnActor]}>{item?.actorName || "-"}</Text>
                  <Text style={[styles.cellText, styles.columnOwner]}>{getTimelineOwnerText(item)}</Text>
                  <Text style={[styles.cellTextStrong, styles.columnStatus]}>{`${item?.status || "-"} (${item?.progressPercent || 0}%)`}</Text>
                  <Text style={[styles.cellText, styles.columnNote]}>{getTimelineNoteText(item)}</Text>
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>
                No timeline activity is available yet. Entries will appear here after the task is created, forwarded, or updated.
              </Text>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatTaskDate(generatedAt, true)}</Text>
          <Text style={styles.footerText}>CRM Task Desk Standard Report | Powered by BanglaFire</Text>
        </View>
      </Page>
    </Document>
  );
}
